import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, resolveImageUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Pencil, Plus } from "lucide-react";
import { timeAgo } from "@/lib/time";

const CATEGORIES = ["Stress reset", "Focus and clarity", "Pre-performance", "Recovery", "Sleep and downregulation", "Anxiety support"];
const DIFFICULTIES = ["Beginner", "Intermediate", "Advanced"];

export default function Admin() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-12">
      <p className="overline text-primary mb-2">Admin</p>
      <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">Coach console</h1>
      <p className="text-muted-foreground mt-2 max-w-xl text-sm sm:text-base">Manage clients, bookings, enquiries, and the practice library.</p>

      <Tabs defaultValue="bookings" className="mt-8 sm:mt-10">
        <div className="-mx-4 sm:mx-0 overflow-x-auto no-scrollbar">
          <TabsList className="bg-muted w-max sm:w-auto mx-4 sm:mx-0">
            <TabsTrigger value="bookings" data-testid="admin-tab-bookings">Bookings</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="admin-tab-enquiries">Enquiries</TabsTrigger>
            <TabsTrigger value="clients" data-testid="admin-tab-clients">Clients</TabsTrigger>
            <TabsTrigger value="practices" data-testid="admin-tab-practices">Practices</TabsTrigger>
            <TabsTrigger value="videos" data-testid="admin-tab-videos">Videos</TabsTrigger>
            <TabsTrigger value="articles" data-testid="admin-tab-articles">Articles</TabsTrigger>
            <TabsTrigger value="leads" data-testid="admin-tab-leads">Leads</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="bookings" className="mt-6"><Bookings /></TabsContent>
        <TabsContent value="enquiries" className="mt-6"><Enquiries /></TabsContent>
        <TabsContent value="clients" className="mt-6"><Clients /></TabsContent>
        <TabsContent value="practices" className="mt-6"><Practices /></TabsContent>
        <TabsContent value="videos" className="mt-6"><Videos /></TabsContent>
        <TabsContent value="articles" className="mt-6"><Articles /></TabsContent>
        <TabsContent value="leads" className="mt-6"><Leads /></TabsContent>
      </Tabs>
    </div>
  );
}

function Bookings() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("active");
  const load = () => api.get("/bookings").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const colors = { pending: "secondary", confirmed: "default", completed: "default", cancelled: "destructive" };
  const isActive = (b) => b.status === "pending" || b.status === "confirmed";
  const activeCount = items.filter(isActive).length;
  const archivedCount = items.length - activeCount;
  const filtered = items.filter((b) => {
    if (filter === "all") return true;
    if (filter === "archived") return !isActive(b);
    return isActive(b); // active
  });
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {[
          { key: "active", label: `Active (${activeCount})` },
          { key: "archived", label: `Archived (${archivedCount})` },
          { key: "all", label: `All (${items.length})` },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`booking-filter-${f.key}`}
            className={`text-xs font-medium px-4 py-2 rounded-full border transition-colors ${
              filter === f.key ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:border-primary/40"
            }`}
          >{f.label}</button>
        ))}
      </div>
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]" data-testid="admin-bookings-table">
            <thead className="bg-muted"><tr className="text-left">
              <Th>Date</Th><Th>Type</Th><Th>Name</Th><Th>Contact</Th><Th>Status</Th><Th>Actions</Th>
            </tr></thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.id} className="border-t border-border">
                  <Td>{b.preferred_date} {b.preferred_time}</Td>
                  <Td>{b.session_type}</Td>
                  <Td>{b.name}</Td>
                  <Td><div className="text-xs">{b.email}<br />{b.phone}</div></Td>
                  <Td><Badge variant={colors[b.status]}>{b.status}</Badge></Td>
                  <Td>
                    <div className="flex gap-1 flex-wrap">
                      {b.status === "pending" && (
                        <Button size="sm" variant="outline" onClick={() => updateStatus(b.id, "confirmed")} data-testid={`book-confirm-${b.id}`}>Confirm</Button>
                      )}
                      {(b.status === "pending" || b.status === "confirmed") && (
                        <Button size="sm" variant="outline" onClick={() => updateStatus(b.id, "completed")} data-testid={`book-complete-${b.id}`}>Complete</Button>
                      )}
                      {(b.status === "pending" || b.status === "confirmed") && (
                        <Button size="sm" variant="ghost" onClick={() => updateStatus(b.id, "cancelled")} data-testid={`book-cancel-${b.id}`}>Cancel</Button>
                      )}
                      {(b.status === "completed" || b.status === "cancelled") && (
                        <Button size="sm" variant="outline" onClick={() => updateStatus(b.id, "pending")} data-testid={`book-reopen-${b.id}`}>Reopen</Button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground text-sm">
                {filter === "active" ? "No active bookings." : filter === "archived" ? "No archived bookings yet." : "No bookings yet."}
              </td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Enquiries() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("active");
  const load = () => api.get("/corporate-enquiries").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const updateStatus = async (id, status) => {
    try { await api.patch(`/corporate-enquiries/${id}`, { status }); toast.success("Updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const activeCount = items.filter((e) => e.status !== "closed").length;
  const closedCount = items.filter((e) => e.status === "closed").length;
  const filtered = items.filter((e) => {
    if (filter === "all") return true;
    if (filter === "closed") return e.status === "closed";
    return e.status !== "closed"; // active
  });
  const statusVariant = (s) => s === "new" ? "default" : s === "contacted" ? "secondary" : "outline";
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {[
          { key: "active", label: `Active (${activeCount})` },
          { key: "closed", label: `Closed (${closedCount})` },
          { key: "all", label: `All (${items.length})` },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`enquiry-filter-${f.key}`}
            className={`text-xs font-medium px-4 py-2 rounded-full border transition-colors ${
              filter === f.key ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:border-primary/40"
            }`}
          >{f.label}</button>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        {filtered.map((e) => (
          <Card key={e.id} className="p-6" data-testid={`enquiry-${e.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-display text-lg font-semibold">{e.company_name}</h3>
                <p className="text-sm text-muted-foreground">{e.contact_person} · {e.team_size} people</p>
              </div>
              <Badge variant={statusVariant(e.status)}>{e.status}</Badge>
            </div>
            <div className="mt-4 text-sm space-y-1">
              <p><strong>Challenge:</strong> {e.main_challenge}</p>
              <p><strong>Workshop:</strong> {e.workshop_type}</p>
              <p className="text-muted-foreground">{e.email} · {e.phone}</p>
              {e.message && <p className="mt-3 text-muted-foreground">{e.message}</p>}
              <p className="text-xs text-muted-foreground pt-2">Received {new Date(e.created_at).toLocaleDateString()}</p>
            </div>
            <div className="mt-4 flex gap-2 flex-wrap">
              {e.status !== "contacted" && e.status !== "closed" && (
                <Button size="sm" variant="outline" onClick={() => updateStatus(e.id, "contacted")} data-testid={`enq-contacted-${e.id}`}>Mark contacted</Button>
              )}
              {e.status !== "closed" && (
                <Button size="sm" variant="ghost" onClick={() => updateStatus(e.id, "closed")} data-testid={`enq-close-${e.id}`}>Hide / Close</Button>
              )}
              {e.status === "closed" && (
                <Button size="sm" variant="outline" onClick={() => updateStatus(e.id, "new")} data-testid={`enq-reopen-${e.id}`}>Reopen</Button>
              )}
            </div>
          </Card>
        ))}
        {filtered.length === 0 && <Card className="p-8 col-span-full text-center text-muted-foreground text-sm">
          {filter === "active" && "No active enquiries. Nice work."}
          {filter === "closed" && "No closed enquiries yet."}
          {filter === "all" && "No enquiries yet."}
        </Card>}
      </div>
    </div>
  );
}

function Clients() {
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState({});
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [resetResult, setResetResult] = useState(null); // {email, password}
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const load = async () => {
    const [u, a] = await Promise.all([
      api.get("/admin/users"),
      api.get("/admin/users/activity"),
    ]);
    setUsers(u.data);
    setActivity(a.data || {});
  };
  useEffect(() => { load(); }, []);
  const viewSessions = async (u) => {
    setSelected(u);
    const r = await api.get(`/admin/users/${u.id}/sessions`);
    setSessions(r.data);
  };
  const setStatus = async (u, status) => {
    try {
      await api.patch(`/admin/users/${u.id}/status`, { status });
      toast.success(`${u.name} marked ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const resetPassword = async (u) => {
    if (!window.confirm(`Reset password for ${u.name}? They will be required to change it after their next login.`)) return;
    setBusyId(u.id);
    try {
      const r = await api.post(`/admin/users/${u.id}/reset-password`);
      setResetResult({ email: r.data.user_email, password: r.data.temporary_password, name: u.name });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };
  const deleteUser = async () => {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`);
      toast.success(`${deleteTarget.name} deleted`);
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };
  const copyTemp = async () => {
    if (!resetResult) return;
    try {
      await navigator.clipboard.writeText(resetResult.password);
      toast.success("Password copied to clipboard");
    } catch { toast.error("Copy failed — select it manually"); }
  };
  const pendingCount = users.filter((u) => u.status === "pending").length;
  const filtered = users.filter((u) => filter === "all" ? true : u.status === filter);
  const statusVariant = (s) => s === "approved" ? "default" : s === "pending" ? "secondary" : "destructive";
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {[
          { key: "all", label: `All (${users.length})` },
          { key: "pending", label: `Pending (${pendingCount})` },
          { key: "approved", label: "Approved" },
          { key: "suspended", label: "Suspended" },
          { key: "rejected", label: "Rejected" },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`client-filter-${f.key}`}
            className={`text-xs font-medium px-4 py-2 rounded-full border transition-colors ${
              filter === f.key ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:border-primary/40"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[880px]" data-testid="admin-clients-table">
          <thead className="bg-muted"><tr className="text-left">
            <Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Status</Th><Th>7d activity</Th><Th>Last login</Th><Th>Joined</Th><Th>Actions</Th>
          </tr></thead>
          <tbody>
            {filtered.map((u) => {
              const count = activity[u.id] || 0;
              const activityClass = count === 0 ? "text-muted-foreground" : count >= 5 ? "text-primary font-semibold" : "text-foreground";
              return (
              <tr key={u.id} className="border-t border-border" data-testid={`client-row-${u.id}`}>
                <Td>{u.name}</Td>
                <Td>{u.email}</Td>
                <Td><Badge variant={u.role === "admin" ? "default" : "secondary"}>{u.role}</Badge></Td>
                <Td><Badge variant={statusVariant(u.status)}>{u.status || "approved"}</Badge></Td>
                <Td>
                  <span className={`text-xs ${activityClass}`} title={`${count} session${count === 1 ? "" : "s"} in last 7 days`} data-testid={`activity-${u.id}`}>
                    {u.role === "admin" ? "—" : `${count} session${count === 1 ? "" : "s"}`}
                  </span>
                </Td>
                <Td>
                  <span className={`text-xs ${u.last_login_at ? "text-foreground" : "text-muted-foreground italic"}`} title={u.last_login_at ? new Date(u.last_login_at).toLocaleString() : ""} data-testid={`last-login-${u.id}`}>
                    {timeAgo(u.last_login_at)}
                  </span>
                </Td>
                <Td>{new Date(u.created_at).toLocaleDateString()}</Td>
                <Td>
                  <div className="flex gap-1 flex-wrap">
                    {u.role !== "admin" && u.status !== "approved" && (
                      <Button size="sm" variant="outline" onClick={() => setStatus(u, "approved")} data-testid={`approve-${u.id}`}>Approve</Button>
                    )}
                    {u.role !== "admin" && u.status === "approved" && (
                      <Button size="sm" variant="ghost" onClick={() => setStatus(u, "suspended")} data-testid={`suspend-${u.id}`}>Suspend</Button>
                    )}
                    {u.role !== "admin" && u.status === "suspended" && (
                      <Button size="sm" variant="outline" onClick={() => setStatus(u, "approved")} data-testid={`unsuspend-${u.id}`}>Unsuspend</Button>
                    )}
                    {u.role !== "admin" && u.status !== "rejected" && u.status !== "approved" && (
                      <Button size="sm" variant="ghost" onClick={() => setStatus(u, "rejected")} data-testid={`reject-${u.id}`}>Reject</Button>
                    )}
                    {u.role !== "admin" && (
                      <Link to={`/admin/client/${u.id}/plan`}>
                        <Button size="sm" variant="outline" data-testid={`edit-plan-${u.id}`}>Edit Plan</Button>
                      </Link>
                    )}
                    <Button size="sm" variant="outline" onClick={() => viewSessions(u)} data-testid={`view-sessions-${u.id}`}>Progress</Button>
                    {u.role !== "admin" && (
                      <Button size="sm" variant="ghost" onClick={() => resetPassword(u)} disabled={busyId === u.id} data-testid={`reset-${u.id}`}>Reset password</Button>
                    )}
                    {u.role !== "admin" && (
                      <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setDeleteTarget(u)} disabled={busyId === u.id} data-testid={`delete-user-${u.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    )}
                  </div>
                </Td>
              </tr>
            );})}
            {filtered.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground text-sm">No users.</td></tr>}
          </tbody>
        </table>
        </div>
        <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{selected?.name} — Sessions</DialogTitle></DialogHeader>
            <div className="max-h-96 overflow-y-auto">
              {sessions.length === 0 ? <p className="text-sm text-muted-foreground">No sessions logged.</p> :
                <table className="w-full text-sm"><thead><tr><Th>Date</Th><Th>Practice</Th><Th>Stress</Th><Th>Focus</Th></tr></thead>
                  <tbody>{sessions.map((s) => (<tr key={s.id} className="border-t border-border">
                    <Td>{new Date(s.completed_at).toLocaleDateString()}</Td><Td>{s.practice_title}</Td>
                    <Td>{s.stress_before}→{s.stress_after}</Td><Td>{s.focus_before}→{s.focus_after}</Td>
                  </tr>))}</tbody>
                </table>}
            </div>
          </DialogContent>
        </Dialog>

        {/* Reset password result dialog */}
        <Dialog open={!!resetResult} onOpenChange={(o) => !o && setResetResult(null)}>
          <DialogContent className="max-w-md" data-testid="reset-result-dialog">
            <DialogHeader>
              <DialogTitle>Password reset for {resetResult?.name}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">Copy this temporary password and share it securely. The user will be required to change it after login. <strong>This is shown only once.</strong></p>
            <div className="mt-4">
              <Label className="text-xs">Email</Label>
              <Input readOnly value={resetResult?.email || ""} className="font-mono text-sm" />
            </div>
            <div className="mt-3">
              <Label className="text-xs">Temporary password</Label>
              <Input readOnly value={resetResult?.password || ""} className="font-mono text-sm" data-testid="reset-temp-password" />
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setResetResult(null)} data-testid="reset-close-btn">Close</Button>
              <Button onClick={copyTemp} data-testid="reset-copy-btn">Copy password</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete confirmation */}
        <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
          <DialogContent className="max-w-md" data-testid="delete-confirm-dialog">
            <DialogHeader>
              <DialogTitle>Delete {deleteTarget?.name}?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              This permanently removes <strong>{deleteTarget?.email}</strong>, their plan, session logs, and bookings. This cannot be undone.
            </p>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button variant="destructive" onClick={deleteUser} disabled={busyId === deleteTarget?.id} data-testid="delete-confirm-btn">
                {busyId === deleteTarget?.id ? "Deleting..." : "Delete permanently"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Card>
    </div>
  );
}

const blankPractice = { title: "", category: CATEGORIES[0], duration_minutes: 5, difficulty: "Beginner", purpose: "", instructions: [""], inhale_seconds: 4, hold_seconds: 4, exhale_seconds: 6, cycles: 8 };

function Practices() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/practices").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const startNew = () => { setEditing({ ...blankPractice }); setOpen(true); };
  const startEdit = (p) => { setEditing({ ...p, instructions: [...p.instructions] }); setOpen(true); };
  const save = async () => {
    const payload = { ...editing, instructions: editing.instructions.filter((i) => i.trim()) };
    try {
      if (payload.id) await api.put(`/practices/${payload.id}`, payload);
      else await api.post("/practices", payload);
      toast.success("Saved");
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this practice?")) return;
    await api.delete(`/practices/${id}`); toast.success("Deleted"); load();
  };
  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={startNew} className="rounded-full" data-testid="admin-add-practice"><Plus className="h-4 w-4 mr-1" /> New practice</Button>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        {items.map((p) => (
          <Card key={p.id} className="p-5 flex justify-between" data-testid={`admin-practice-${p.id}`}>
            <div>
              <div className="flex gap-2 items-center mb-1"><Badge variant="secondary">{p.category}</Badge><span className="text-xs text-muted-foreground">{p.duration_minutes} min · {p.difficulty}</span></div>
              <h3 className="font-display font-semibold">{p.title}</h3>
              <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{p.purpose}</p>
            </div>
            <div className="flex flex-col gap-1">
              <Button size="sm" variant="ghost" onClick={() => startEdit(p)} data-testid={`edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => remove(p.id)} data-testid={`delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit practice" : "New practice"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Title</Label><Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="practice-title-input" /></div>
                <div><Label>Category</Label>
                  <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                    <SelectTrigger data-testid="practice-category-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Duration (min)</Label><Input type="number" value={editing.duration_minutes} onChange={(e) => setEditing({ ...editing, duration_minutes: parseInt(e.target.value) || 1 })} /></div>
                <div><Label>Difficulty</Label>
                  <Select value={editing.difficulty} onValueChange={(v) => setEditing({ ...editing, difficulty: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{DIFFICULTIES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Inhale sec</Label><Input type="number" value={editing.inhale_seconds} onChange={(e) => setEditing({ ...editing, inhale_seconds: parseInt(e.target.value) || 0 })} /></div>
                <div><Label>Hold sec</Label><Input type="number" value={editing.hold_seconds} onChange={(e) => setEditing({ ...editing, hold_seconds: parseInt(e.target.value) || 0 })} /></div>
                <div><Label>Exhale sec</Label><Input type="number" value={editing.exhale_seconds} onChange={(e) => setEditing({ ...editing, exhale_seconds: parseInt(e.target.value) || 0 })} /></div>
                <div><Label>Cycles</Label><Input type="number" value={editing.cycles} onChange={(e) => setEditing({ ...editing, cycles: parseInt(e.target.value) || 1 })} /></div>
              </div>
              <div><Label>Purpose</Label><Textarea value={editing.purpose} onChange={(e) => setEditing({ ...editing, purpose: e.target.value })} rows={2} /></div>
              <div>
                <Label>Instructions (one per line)</Label>
                <Textarea value={editing.instructions.join("\n")} onChange={(e) => setEditing({ ...editing, instructions: e.target.value.split("\n") })} rows={5} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="practice-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const blankVideo = { title: "", description: "", url: "", category: "General", thumbnail_url: "" };

function Videos() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/videos").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const startNew = () => { setEditing({ ...blankVideo }); setOpen(true); };
  const startEdit = (v) => { setEditing({ ...v }); setOpen(true); };
  const save = async () => {
    if (!editing.title.trim() || !editing.url.trim()) {
      toast.error("Title and URL are required");
      return;
    }
    try {
      if (editing.id) await api.put(`/videos/${editing.id}`, editing);
      else await api.post("/videos", editing);
      toast.success("Saved");
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    await api.delete(`/videos/${id}`); toast.success("Deleted"); load();
  };
  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={startNew} className="rounded-full" data-testid="admin-add-video"><Plus className="h-4 w-4 mr-1" /> New video</Button>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        {items.map((v) => (
          <Card key={v.id} className="p-5 flex justify-between gap-3" data-testid={`admin-video-${v.id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex gap-2 items-center mb-1">
                <Badge variant="secondary">{v.category || "General"}</Badge>
                <a href={v.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary truncate hover:underline">{v.url}</a>
              </div>
              <h3 className="font-display font-semibold">{v.title}</h3>
              {v.description && <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{v.description}</p>}
            </div>
            <div className="flex flex-col gap-1">
              <Button size="sm" variant="ghost" onClick={() => startEdit(v)} data-testid={`edit-video-${v.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => remove(v.id)} data-testid={`delete-video-${v.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </Card>
        ))}
        {items.length === 0 && <Card className="p-8 col-span-full text-center text-muted-foreground text-sm">No videos yet. Click "New video" to add one.</Card>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="admin-video-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit video" : "New video"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div><Label>Title</Label><Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="video-title-input" /></div>
              <div><Label>Video URL (YouTube, Vimeo, Google Drive, or any link)</Label><Input value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} placeholder="https://drive.google.com/file/d/..." data-testid="video-url-input" /></div>
              <div><Label>Category</Label><Input value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} placeholder="e.g. Foundations, Performance, Recovery" data-testid="video-category-input" /></div>
              <div><Label>Thumbnail URL (optional — auto-detected for YouTube)</Label><Input value={editing.thumbnail_url} onChange={(e) => setEditing({ ...editing, thumbnail_url: e.target.value })} placeholder="https://..." /></div>
              <div><Label>Description</Label><Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={3} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="video-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Leads() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/leads").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (lead) => {
    if (!window.confirm(`Remove ${lead.email} from the lead list?`)) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success("Lead removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const copyAll = async () => {
    const emails = items.map((l) => l.email).join(", ");
    try {
      await navigator.clipboard.writeText(emails);
      toast.success(`Copied ${items.length} email${items.length === 1 ? "" : "s"}`);
    } catch { toast.error("Copy failed"); }
  };
  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button size="sm" variant="outline" onClick={copyAll} disabled={items.length === 0} data-testid="leads-copy-all-btn">Copy all emails</Button>
      </div>
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[520px]" data-testid="admin-leads-table">
          <thead className="bg-muted"><tr className="text-left"><Th>Email</Th><Th>Source</Th><Th>Date</Th><Th></Th></tr></thead>
          <tbody>
            {items.map((l) => (
              <tr key={l.id} className="border-t border-border" data-testid={`lead-row-${l.id}`}>
                <Td>{l.email}</Td>
                <Td>{l.source}</Td>
                <Td>{new Date(l.created_at).toLocaleString()}</Td>
                <Td>
                  <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => remove(l)} data-testid={`lead-delete-${l.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </Td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground text-sm">No leads yet.</td></tr>}
          </tbody>
        </table>
        </div>
      </Card>
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{children}</th>;
const Td = ({ children }) => <td className="px-4 py-3 align-top">{children}</td>;

const blankArticle = { title: "", excerpt: "", body: "", author: "Evan Gill", cover_image_url: "", is_published: false };

function Articles() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const load = () => api.get("/admin/articles").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const startNew = () => { setEditing({ ...blankArticle }); setOpen(true); };
  const startEdit = (a) => { setEditing({ ...a, cover_image_url: a.cover_image_url || "" }); setOpen(true); };
  const uploadCover = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await api.post("/admin/articles/upload-cover", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((cur) => ({ ...cur, cover_image_url: r.data.url }));
      toast.success("Cover image uploaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };
  const save = async () => {
    if (!editing.title.trim() || !editing.body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    const payload = {
      title: editing.title,
      excerpt: editing.excerpt || "",
      body: editing.body,
      author: editing.author || "Evan Gill",
      cover_image_url: editing.cover_image_url || "",
      is_published: !!editing.is_published,
    };
    try {
      if (editing.id) await api.put(`/admin/articles/${editing.id}`, payload);
      else await api.post("/admin/articles", payload);
      toast.success(editing.is_published ? "Published" : "Saved as draft");
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/articles/${a.id}`);
      toast.success("Article deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={startNew} className="rounded-full" data-testid="admin-add-article"><Plus className="h-4 w-4 mr-1" /> New article</Button>
      </div>
      <div className="grid gap-4">
        {items.map((a) => (
          <Card key={a.id} className="p-5 flex justify-between gap-3" data-testid={`admin-article-${a.id}`}>
            <div className="flex gap-4 min-w-0 flex-1">
              {a.cover_image_url && (
                <img
                  src={resolveImageUrl(a.cover_image_url)}
                  alt=""
                  className="h-16 w-24 object-cover rounded-md shrink-0 bg-muted"
                  loading="lazy"
                />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex gap-2 items-center mb-1 flex-wrap">
                  {a.is_published ? (
                    <Badge className="bg-primary text-primary-foreground">Published</Badge>
                  ) : (
                    <Badge variant="secondary">Draft</Badge>
                  )}
                  <span className="text-xs text-muted-foreground">/articles/{a.slug}</span>
                  <span className="text-xs text-muted-foreground">· Updated {timeAgo(a.updated_at)}</span>
                </div>
                <h3 className="font-display font-semibold text-lg">{a.title}</h3>
                {a.excerpt && <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{a.excerpt}</p>}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              {a.is_published && (
                <a href={`/articles/${a.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`view-article-${a.id}`}>
                  <Button size="sm" variant="ghost">View</Button>
                </a>
              )}
              <Button size="sm" variant="ghost" onClick={() => startEdit(a)} data-testid={`edit-article-${a.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => remove(a)} data-testid={`delete-article-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </Card>
        ))}
        {items.length === 0 && <Card className="p-8 text-center text-muted-foreground text-sm">No articles yet. Click "New article" to publish your first one.</Card>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-article-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit article" : "New article"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="article-title-input" />
              </div>
              <div>
                <Label>Author</Label>
                <Input value={editing.author || ""} onChange={(e) => setEditing({ ...editing, author: e.target.value })} placeholder="Evan Gill" data-testid="article-author-input" />
              </div>
              <div>
                <Label>Cover image <span className="text-xs text-muted-foreground">(optional — JPEG / PNG / WEBP, max 5 MB)</span></Label>
                <div className="mt-2 flex items-start gap-4">
                  {editing.cover_image_url ? (
                    <div className="relative">
                      <img
                        src={resolveImageUrl(editing.cover_image_url)}
                        alt="Cover preview"
                        className="h-24 w-40 object-cover rounded-md border border-border bg-muted"
                        data-testid="article-cover-preview"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="absolute -top-2 -right-2 h-6 w-6 p-0 bg-background border border-border rounded-full text-destructive hover:text-destructive"
                        onClick={() => setEditing({ ...editing, cover_image_url: "" })}
                        data-testid="article-cover-remove"
                      ><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  ) : (
                    <div className="h-24 w-40 rounded-md border border-dashed border-border bg-muted/40 flex items-center justify-center text-xs text-muted-foreground">No image</div>
                  )}
                  <div className="flex-1">
                    <input
                      id="article-cover-file"
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={(e) => uploadCover(e.target.files?.[0])}
                      data-testid="article-cover-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={uploading}
                      onClick={() => document.getElementById("article-cover-file").click()}
                      data-testid="article-cover-upload-btn"
                    >{uploading ? "Uploading…" : editing.cover_image_url ? "Replace image" : "Upload image"}</Button>
                    <p className="text-xs text-muted-foreground mt-2">Recommended ratio 16:9. The image is hosted on Emergent Object Storage and served publicly.</p>
                  </div>
                </div>
              </div>
              <div>
                <Label>Excerpt <span className="text-xs text-muted-foreground">(short summary shown on the list)</span></Label>
                <Textarea value={editing.excerpt || ""} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} rows={2} data-testid="article-excerpt-input" />
              </div>
              <div>
                <Label>Body</Label>
                <Textarea value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} rows={14} placeholder="Write the full article here…" data-testid="article-body-input" />
                <p className="text-xs text-muted-foreground mt-1">Plain text with paragraphs (line breaks are preserved).</p>
              </div>
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!editing.is_published}
                  onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })}
                  className="h-4 w-4 accent-primary"
                  data-testid="article-published-checkbox"
                />
                <span className="text-sm font-medium">Published <span className="text-muted-foreground font-normal">(visible to everyone — uncheck to keep as draft)</span></span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="article-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
